// SeedingSkeleton — shown on a freshly-created release / feature-launch
// detail page while the background LLM pass fills its fields. It replaces the
// editor entirely (not just an overlay) so there's nothing to accidentally
// type into before the seed lands and overwrites it. The detail page polls
// on `seeding_at` and swaps to the real editor the moment it clears.
//
// `onBypass` (optional) surfaces an escape hatch after a while, in case the
// seed hangs — it lets the caller drop the skeleton and show the editor.
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Spinner } from "@/ui/spinner";

export function SeedingSkeleton({
  message,
  onBypass,
}: {
  message: string;
  onBypass?: () => void;
}) {
  const [showBypass, setShowBypass] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setShowBypass(true), 25_000);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div className="space-y-6 py-6">
      <Link
        href="/timeline"
        className="inline-flex items-center gap-1 text-sm text-text-secondary no-underline hover:text-text"
      >
        ← Timeline
      </Link>

      <div className="flex items-center gap-2.5 rounded-lg border border-border bg-card/60 px-4 py-3 text-sm text-text-secondary">
        <Spinner size="sm" className="text-accent" />
        {message}
      </div>

      {/* Ghost of the detail layout: title, blurb, a meta card, a body block. */}
      <div
        className="animate-pulse space-y-6 motion-reduce:animate-none"
        aria-hidden
      >
        <div className="h-8 w-2/3 rounded bg-border" />
        <div className="space-y-2">
          <div className="h-3 w-full rounded bg-border/70" />
          <div className="h-3 w-5/6 rounded bg-border/70" />
        </div>
        <div className="space-y-3 rounded-lg border border-border bg-card p-5">
          {["a", "b", "c", "d"].map((k) => (
            <div key={k} className="flex gap-4">
              <div className="h-3 w-24 rounded bg-border" />
              <div className="h-3 flex-1 rounded bg-border/60" />
            </div>
          ))}
        </div>
        <div className="space-y-2 rounded-lg border border-border bg-card p-5">
          <div className="h-3 w-1/3 rounded bg-border" />
          <div className="h-3 w-full rounded bg-border/60" />
          <div className="h-3 w-4/5 rounded bg-border/60" />
        </div>
      </div>

      {showBypass && onBypass && (
        <p className="text-xs text-text-tertiary">
          Taking longer than usual.{" "}
          <button
            type="button"
            onClick={onBypass}
            className="underline decoration-dotted hover:text-text"
          >
            Edit manually instead
          </button>
        </p>
      )}
    </div>
  );
}
